import { FRANCHISE_COLUMNS, GAME_COLUMNS, TROPHY_SET_COLUMNS, } from './database.types.js';
import { mapFranchiseRow, mapGameRow, mapTrophySetRow } from './mapping.js';
import { buildGameQueryPlan, sliceToPage, MAX_LIMIT } from './game-query.js';
/**
 * Erste echte Supabase-Implementierung.
 *
 * Regeln, die hier eingebaut sind und nicht umgangen werden koennen:
 *  - kein getAllGames(): es existiert keine Methode ohne Limit
 *  - limit ist hart auf MAX_LIMIT (50) gedeckelt
 *  - Keyset-Pagination ueber (title, id), kein OFFSET
 *  - Franchise-Gruppierung passiert serverseitig ueber die sortierte Abfrage
 *  - Rueckgabewerte sind Domain Types, niemals DB-Zeilen
 */
export class SupabaseGameRepository {
    client;
    constructor(client) {
        this.client = client;
    }
    async getGameById(gameId) {
        const { data, error } = await this.client
            .from('game')
            .select(GAME_COLUMNS)
            .eq('id', gameId)
            .maybeSingle();
        if (error)
            throw new Error(`getGameById fehlgeschlagen: ${error.message}`);
        return data ? mapGameRow(data) : null;
    }
    async getFranchise(franchiseId) {
        const { data, error } = await this.client
            .from('franchise')
            .select(FRANCHISE_COLUMNS)
            .eq('id', franchiseId)
            .maybeSingle();
        if (error)
            throw new Error(`getFranchise fehlgeschlagen: ${error.message}`);
        return data ? mapFranchiseRow(data) : null;
    }
    /** Alle Trophy Sets eines Games. Sets werden nie zusammengefuehrt. */
    async getGameTrophySets(gameId) {
        const { data, error } = await this.client
            .from('trophy_set')
            .select(TROPHY_SET_COLUMNS)
            .eq('game_id', gameId)
            .order('platform', { ascending: true })
            .returns();
        if (error)
            throw new Error(`getGameTrophySets fehlgeschlagen: ${error.message}`);
        return (data ?? []).map(mapTrophySetRow);
    }
    async searchGames(request) {
        const plan = buildGameQueryPlan(request);
        let query = this.client.from('game').select(GAME_COLUMNS);
        if (plan.search) {
            // Teilstringsuche, serverseitig. Nutzt den Trigram-Index aus 0009.
            query = query.ilike('title', `%${plan.search}%`);
        }
        if (plan.platform) {
            query = query.contains('platforms', [plan.platform]);
        }
        if (plan.supportState) {
            query = query.eq('support_state', plan.supportState);
        }
        if (plan.keysetOr) {
            query = query.or(plan.keysetOr);
        }
        const { data, error } = await query
            .order('title', { ascending: true })
            .order('id', { ascending: true })
            .limit(plan.fetchLimit)
            .returns();
        if (error)
            throw new Error(`searchGames fehlgeschlagen: ${error.message}`);
        const page = sliceToPage(data ?? [], plan.limit);
        return {
            items: page.items.map(mapGameRow),
            nextCursor: page.nextCursor,
            hasMore: page.hasMore,
        };
    }
    /** Katalogliste ohne Suchbegriff. Gleiche Pagination, gleiche Obergrenze. */
    listGamesPaginated(request) {
        return this.searchGames({
            query: '',
            platform: null,
            supportState: null,
            groupByFranchise: false,
            cursor: request.cursor,
            limit: request.limit,
        });
    }
    /**
     * Serverseitige Franchise-Gruppierung: die Datenbank liefert nach
     * (Franchise, Titel) sortiert, der Client gruppiert nicht selbst und filtert
     * nicht selbst. Paginiert wird weiterhin ueber Spiele, nicht ueber Gruppen –
     * sonst waere die Seitengroesse unvorhersehbar.
     */
    async searchGamesGroupedByFranchise(request) {
        const page = await this.searchGames(request);
        const franchiseIds = [
            ...new Set(page.items.map((g) => g.franchiseId).filter((id) => id !== null)),
        ];
        const names = new Map();
        if (franchiseIds.length > 0) {
            const { data, error } = await this.client
                .from('franchise')
                .select('id, name')
                .in('id', franchiseIds)
                .returns();
            if (error)
                throw new Error(`Franchise-Namen laden fehlgeschlagen: ${error.message}`);
            for (const row of data ?? [])
                names.set(row.id, row.name);
        }
        const groups = new Map();
        for (const game of page.items) {
            const key = game.franchiseId;
            const existing = groups.get(key);
            if (existing) {
                existing.games.push(game);
            }
            else {
                groups.set(key, {
                    franchiseId: key,
                    franchiseName: key ? (names.get(key) ?? null) : null,
                    games: [game],
                });
            }
        }
        return {
            items: [...groups.values()],
            nextCursor: page.nextCursor,
            hasMore: page.hasMore,
        };
    }
    async listRecentlyViewed(userId, request) {
        // Ohne Account gibt es keine serverseitige Historie – der Gast fuehrt sie
        // lokal. Es wird bewusst nichts erfunden.
        if (!userId)
            return { items: [], nextCursor: null, hasMore: false };
        const limit = Math.min(Math.max(request.limit, 1), MAX_LIMIT);
        const { data, error } = await this.client
            .from('user_game_run')
            .select('trophy_set:trophy_set_id(game_id)')
            .order('last_activity_at', { ascending: false, nullsFirst: false })
            .limit(limit)
            .returns();
        if (error)
            throw new Error(`listRecentlyViewed fehlgeschlagen: ${error.message}`);
        const gameIds = [
            ...new Set((data ?? []).map((row) => row.trophy_set?.game_id).filter(Boolean)),
        ];
        if (gameIds.length === 0)
            return { items: [], nextCursor: null, hasMore: false };
        const games = await this.client
            .from('game')
            .select(GAME_COLUMNS)
            .in('id', gameIds)
            .returns();
        if (games.error)
            throw new Error(`listRecentlyViewed fehlgeschlagen: ${games.error.message}`);
        const byId = new Map((games.data ?? []).map((row) => [row.id, mapGameRow(row)]));
        return {
            items: gameIds.map((id) => byId.get(id)).filter((g) => Boolean(g)),
            nextCursor: null,
            hasMore: false,
        };
    }
}
//# sourceMappingURL=game-repository.js.map